/**
 * Retire a COMPED booking. A dialog rather than a one-click action because the
 * reason is required and a bare button has nowhere to put it.
 *
 * The callout follows `ReleaseReservationDialog` rather than the refund
 * drawer's muted footer: the fact a CSR would not expect is that nobody tells
 * the customer, so it gets the prominent treatment.
 *
 * The helpers below are lowercase functions, not components —
 * `react/no-multi-comp` runs with `ignoreStateless: false`.
 */
import { X } from 'lucide-react';
import { useRef } from 'react';

import type { BookingDetail } from '../../api/bookingsV2';
import { useFocusTrap } from '../../hooks/useFocusTrap';

import { customerDisplayName } from './bookingDetailModel';
import { useCancelBookingSubmit } from './useCancelBookingSubmit';

function dialogHeader(onClose: () => void, disabled: boolean) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-stone-200 px-6 py-4 dark:border-stone-700">
      <div>
        <h2
          id="cancel-booking-title"
          className="text-base font-semibold text-bark-900 dark:text-stone-100"
        >
          Cancel this booking
        </h2>
        <p className="mt-0.5 text-sm text-stone-500 dark:text-stone-400">
          It was comped, so there is nothing to refund.
        </p>
      </div>
      <button
        type="button"
        onClick={disabled ? undefined : onClose}
        disabled={disabled}
        aria-label="Close"
        className="rounded-lg p-1 text-stone-400 transition hover:bg-stone-100 hover:text-stone-600 disabled:cursor-not-allowed dark:hover:bg-stone-700/50"
      >
        <X size={18} />
      </button>
    </div>
  );
}

function reasonField(s: ReturnType<typeof useCancelBookingSubmit>) {
  return (
    <div>
      <label
        htmlFor="cancel-reason"
        className="block text-sm font-medium text-bark-900 dark:text-stone-100"
      >
        Reason <span className="text-red-600">*</span>
      </label>
      <textarea
        id="cancel-reason"
        ref={s.reasonRef}
        value={s.reason}
        onChange={(e) => s.setReason(e.target.value)}
        rows={3}
        maxLength={500}
        aria-invalid={s.error ? true : undefined}
        aria-describedby={s.error ? 'cancel-reason-error cancel-reason-help' : 'cancel-reason-help'}
        placeholder="Why is this comp being cancelled?"
        className="mt-1.5 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm text-bark-900 outline-none transition focus:border-moss-400 dark:border-stone-700 dark:bg-stone-800/50 dark:text-stone-100"
      />
      <p id="cancel-reason-help" className="mt-1.5 text-xs text-stone-500 dark:text-stone-400">
        Saved to this booking&rsquo;s history for staff. The customer never sees it.
      </p>
      {s.error && (
        <p id="cancel-reason-error" role="alert" className="mt-1.5 text-sm text-red-600">
          {s.error}
        </p>
      )}
    </div>
  );
}

export function CancelBookingDialog({
  booking,
  onClose,
  onCancelled,
}: {
  booking: BookingDetail;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const s = useCancelBookingSubmit(booking, onClose, onCancelled);
  const panelRef = useRef<HTMLDivElement>(null);
  // No autoFocusFirst — useCancelBookingSubmit already puts the caret in the
  // reason field, and claiming focus twice fights it.
  useFocusTrap(panelRef);

  return (
    <div
      role="presentation"
      onClick={s.dismiss}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-booking-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-[#1e2521]"
      >
        {dialogHeader(s.dismiss, s.submitting)}
        <div className="space-y-4 px-6 py-5">
          <div className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
            {booking.money.ticketsCount} {booking.money.ticketsCount === 1 ? 'seat' : 'seats'} go
            back on sale immediately. No money moves and no refund is recorded.{' '}
            {customerDisplayName(booking)} is not emailed.
          </div>
          {reasonField(s)}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-6 py-4 dark:border-stone-700">
          <button
            type="button"
            onClick={s.dismiss}
            disabled={s.submitting}
            className="rounded-lg px-3 py-2 text-sm font-medium text-stone-600 transition hover:bg-stone-100 disabled:cursor-not-allowed dark:text-stone-300 dark:hover:bg-stone-700/50"
          >
            Keep booking
          </button>
          <button
            type="button"
            onClick={() => void s.submit()}
            disabled={!s.canSubmit}
            className="rounded-lg bg-moss-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-moss-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {s.submitting ? 'Cancelling…' : 'Cancel booking'}
          </button>
        </div>
      </div>
    </div>
  );
}
